import { createReducer } from 'typesafe-actions';
import { defaultErrorHandler, defaultRequestHandler } from '../dataFetching/generators';
import {
    LooksRawData,
    LooksListingProcessedData,
    LookListingState,
    LooksListingDictionary,
    LookListing,
} from './types';
import {
    fetchLooksPageActions,
    fetchNoLooksFoundActions,
    LooksPageActionsType,
    resetLooksListingAction,
} from './actions';

export const mapLooksData = (data: LooksRawData): LooksListingProcessedData => {
    const { meta, looks, filters, pagination, seo } = data;
    const looksData = looks.map(look => ({
        name: look.name,
        caption: look.caption,
        trackingName: look.trackingName || '',
        slug: look.slug,
        color: look.color,
        isLandscape: look.isLandscape,
        landscapeImage: look.landscapeListingImage?.seo_url,
        portraitImage: look.portraitListingImage?.seo_url,
        roomName: look.room?.name || '',
        roomTrackingName: look.room?.trackingName || '',
        roomSlug: look.room?.slug || '',
    }));

    const roomFiltersData = filters.rooms.map(room => ({
        id: room.id,
        name: room.name,
        trackingName: room.trackingName,
        slug: room.slug,
        pageTitle: room.pageTitle,
        pageDescription: room.pageDescription,
        isSelected: room.selected,
    }));

    const styleFiltersData = filters.styles.map(style => ({
        id: style.id,
        name: style.name,
        trackingName: style.trackingName,
        slug: style.slug,
        selected: style.selected,
    }));

    const looksDataWithPage = {
        [pagination.page]: looksData,
    };

    const seoData = {
        metaDescription: seo?.metaDescription,
        metaTitle: seo?.metaTitle,
        hreflangs: seo?.hreflangs,
        indexFollow: seo?.indexFollow || false,
    };

    return {
        meta,
        orderedListing: looksDataWithPage,
        listing: [],
        filters: {
            rooms: roomFiltersData || [],
            styles: styleFiltersData || [],
        },
        total: pagination?.total || 0,
        perPage: pagination?.perPage || 10,
        seoData,
    };
};

export const generateListingPlaceholder = (count: number): LookListing[] => {
    return new Array(count).fill({}).map((item, index) => ({
        name: '',
        caption: '',
        trackingName: '',
        slug: `prefill-${index}`,
        isLandscape: false,
        color: '',
        landscapeImage: '',
        portraitImage: '',
        roomName: ' ',
        roomTrackingName: '',
        roomSlug: '',
    }));
};

export const defaultLooksPageState: LookListingState = {
    isFetching: false,
    didInvalidate: false,
    identifier: '',
    looks: {
        meta: {
            name: '',
            caption: '',
            metaDescription: '',
            trackingName: '',
        },
        orderedListing: {},
        listing: [],
        filters: {
            rooms: [],
            styles: [],
        },
        total: 0,
        seoData: {
            metaTitle: '',
            metaDescription: '',
            hreflangs: {},
            indexFollow: false,
        },
    },
    noLooksFound: {
        title: '',
        subtitle: '',
    },
};

const getInitialState = (state: LookListingState) => ({
    ...state,
    looks: {
        ...state.looks,
        listing: generateListingPlaceholder(12),
        orderedListing: {},
    },
});

const sortLooksByPage = (listing: LooksListingDictionary) => {
    return Object.keys(listing)
        .sort()
        .reduce((acc, c) => {
            acc[c] = listing[c];
            return acc;
        }, {});
};

const normalizeLooks = (paginatedLooksData: object) => {
    let looks: LookListing[] = [];
    Object.keys(paginatedLooksData).forEach(page => {
        looks = [...looks, ...paginatedLooksData[page]];
    });
    return looks;
};

export const looksPageReducer = createReducer<LookListingState, LooksPageActionsType>(defaultLooksPageState)
    .handleAction(fetchLooksPageActions.success, (state, action) => {
        const { data: responseData, identifier, reducerPayloadOptions } = action.payload;
        const { meta, orderedListing, filters, total, perPage, seoData } = mapLooksData(responseData.data);
        const lookListing = reducerPayloadOptions?.reset
            ? { ...orderedListing }
            : { ...state.looks.orderedListing, ...orderedListing };
        const sortedLooksData = sortLooksByPage(lookListing);
        return {
            ...state,
            looks: {
                meta,
                orderedListing: sortedLooksData,
                listing: normalizeLooks(sortedLooksData),
                filters,
                total,
                perPage,
                seoData,
            },
            isFetching: false,
            identifier,
        };
    })
    .handleAction(fetchLooksPageActions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(fetchLooksPageActions.request, (state, action) => {
        // initialize on first request with looks placeholders
        if (state.looks.listing.length === 0) {
            return defaultRequestHandler(getInitialState(state), action);
        }
        return defaultRequestHandler(state, action);
    })
    .handleAction(fetchNoLooksFoundActions.success, (state, action) => {
        const { data } = action.payload;

        const noLooksFound = data.data?.[0];

        if (!noLooksFound) {
            return state;
        }

        return {
            ...state,
            noLooksFound,
        };
    })
    .handleAction(fetchNoLooksFoundActions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(resetLooksListingAction, state => getInitialState(state));
